import React, { useState, useEffect } from 'react';

function Players({ players, teams, socket, user }) {
  const [newPlayer, setNewPlayer] = useState({ name: '', team_id: '', position: '', number: '', handedness: '' });
  const [editRows, setEditRows] = useState({});
  const [localPlayers, setLocalPlayers] = useState(players);
  const [deletedPlayers, setDeletedPlayers] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const handlePlayerChange = (e) => {
    setNewPlayer({ ...newPlayer, [e.target.name]: e.target.value });
  };

  const addPlayer = (e) => {
    e.preventDefault();
    if (newPlayer.name.trim() === '' || !socket) return;
    socket.emit('addPlayer', newPlayer);
    setNewPlayer({ name: '', team_id: '', position: '', number: '', handedness: '' });
  };

  useEffect(() => {
    const activePlayers = players.filter((player) => player.deleted !== 1 && player.deleted !== '1');
    setLocalPlayers(activePlayers);
    const rows = {};
    activePlayers.forEach((player) => {
      rows[player.id] = {
        team_id: player.team_id || '',
        position: player.position || '',
        number: player.number ?? '',
        handedness: player.handedness || '',
      };
    });
    setEditRows(rows);
  }, [players]);

  useEffect(() => {
    if (!socket) return;

    const handleDeletedPlayers = (players) => {
      setDeletedPlayers(players);
    };

    socket.on('deletedPlayers', handleDeletedPlayers);
    return () => {
      socket.off('deletedPlayers', handleDeletedPlayers);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !showDeleted) return;
    socket.emit('getDeletedPlayers');
  }, [socket, showDeleted]);

  const updateEditField = (playerId, field, value) => {
    setEditRows((prev) => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: value,
      },
    }));
  };

  const savePlayer = (player) => {
    if (!socket || !socket.connected) return;
    const row = editRows[player.id] || {};
    const updatedPlayer = {
      id: player.id,
      team_id: row.team_id === '' ? null : Number(row.team_id),
      position: row.position || null,
      number: row.number === '' ? null : Number(row.number),
      handedness: row.handedness || null,
    };

    setLocalPlayers((prev) => prev.map((p) => (
      p.id === player.id ? { ...p, ...updatedPlayer } : p
    )));

    socket.emit('updatePlayer', updatedPlayer);
    socket.emit('getInitialData');
  };

  const deletePlayer = (playerId, playerName) => {
    if (!socket || !socket.connected) return;
    const confirmed = window.confirm(`Poistetaanko pelaaja ${playerName}? Pelaaja siirretään poistettuihin.`);
    if (!confirmed) return;

    setLocalPlayers((prev) => prev.filter((p) => p.id !== playerId));
    socket.emit('deletePlayer', playerId);
    socket.emit('getInitialData');
  };

  const restorePlayer = (playerId) => {
    if (!socket || !socket.connected) return;
    socket.emit('restorePlayer', playerId);
    socket.emit('getDeletedPlayers');
    socket.emit('getInitialData');
  };

  const formatPosition = (position) => {
    switch (position) {
      case 'Forward':
        return 'F';
      case 'Defense':
        return 'D';
      case 'Goalie':
        return 'G';
      default:
        return position || '-';
    }
  };

  const canEdit = user && (user.role === 'SuperAdmin' || user.role === 'Admin' || user.role === 'AdminTournament');

  return (
    <div className="players-section">
      <h2>Pelaajat</h2>
      {canEdit && (
        <form onSubmit={addPlayer}>
          <input
            type="text"
            name="name"
            value={newPlayer.name}
            onChange={handlePlayerChange}
            placeholder="Anna uuden pelaajan nimi"
          />
          <input
            type="number"
            name="number"
            value={newPlayer.number}
            onChange={handlePlayerChange}
            placeholder="Pelaajan numero"
            min="0"
          />
          <select name="team_id" value={newPlayer.team_id} onChange={handlePlayerChange}>
            <option value="">Valitse joukkue</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
          <select name="position" value={newPlayer.position} onChange={handlePlayerChange}>
            <option value="">Valitse pelipaikka</option>
            <option value="Forward">Hyökkääjä (F)</option>
            <option value="Defense">Puolustaja (D)</option>
            <option value="Goalie">Maalivahti (G)</option>
          </select>
          <select name="handedness" value={newPlayer.handedness} onChange={handlePlayerChange}>
            <option value="">Valitse kätisyys</option>
            <option value="Left">Vasen</option>
            <option value="Right">Oikea</option>
          </select>
          <button type="submit">Lisää pelaaja</button>
        </form>
      )}

      <div className="players-toolbar">
        <button type="button" onClick={() => {
          setShowDeleted(false);
        }}>
          Aktiiviset pelaajat
        </button>
        <button type="button" onClick={() => {
          setShowDeleted(true);
        }}>
          Poistetut pelaajat
        </button>
      </div>
      {showDeleted ? (
        <div className="players-table-wrapper">
          <h3>Poistetut pelaajat</h3>
          <table className="players-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Nimi</th>
                <th>Joukkue</th>
                <th>Pelipaikka</th>
                <th>Kätisyys</th>
                <th>Maalit</th>
                <th>Syötöt</th>
                {canEdit && <th>Toiminto</th>}
              </tr>
            </thead>
            <tbody>
              {deletedPlayers.map((player) => (
                <tr key={player.id}>
                  <td>{player.number != null && player.number !== '' ? player.number : '-'}</td>
                  <td>{player.name}</td>
                  <td>{player.team_name || 'Ei joukkuetta'}</td>
                  <td>{formatPosition(player.position)}</td>
                  <td>{player.handedness || '-'}</td>
                  <td>{player.goals ?? 0}</td>
                  <td>{player.assists ?? 0}</td>
                  {canEdit && (
                    <td>
                      <button type="button" onClick={() => restorePlayer(player.id)} disabled={!socket}>
                        Palauta
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : localPlayers.length > 0 ? (
        <div className="players-table-wrapper">
          <table className="players-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Nimi</th>
                <th>Joukkue</th>
                <th>Pelipaikka</th>
                <th>Kätisyys</th>
                <th>Maalit</th>
                <th>Syötöt</th>
                {canEdit && <th>Muokkaa</th>}
              </tr>
            </thead>
            <tbody>
              {localPlayers.map((player) => (
                <tr key={player.id}>
                  <td>{player.number != null && player.number !== '' ? player.number : '-'}</td>
                  <td>{player.name}</td>
                  <td>{player.team_name || 'Ei joukkuetta'}</td>
                  <td>{formatPosition(player.position)}</td>
                  <td>{player.handedness || '-'}</td>
                  <td>{player.goals ?? 0}</td>
                  <td>{player.assists ?? 0}</td>
                  {canEdit && (
                    <td>
                      <div className="player-edit-row">
                        <input
                          type="number"
                          value={editRows[player.id]?.number || ''}
                          min="0"
                          placeholder="#"
                          onChange={(e) => updateEditField(player.id, 'number', e.target.value)}
                        />
                        <select
                          value={editRows[player.id]?.team_id || ''}
                          onChange={(e) => updateEditField(player.id, 'team_id', e.target.value)}
                        >
                          <option value="">Valitse joukkue</option>
                          {teams.map(team => (
                            <option key={team.id} value={team.id}>{team.name}</option>
                          ))}
                        </select>
                        <select
                          value={editRows[player.id]?.position || ''}
                          onChange={(e) => updateEditField(player.id, 'position', e.target.value)}
                        >
                          <option value="">Valitse pelipaikka</option>
                          <option value="Forward">Hyökkääjä (F)</option>
                          <option value="Defense">Puolustaja (D)</option>
                          <option value="Goalie">Maalivahti (G)</option>
                        </select>
                        <select
                          value={editRows[player.id]?.handedness || ''}
                          onChange={(e) => updateEditField(player.id, 'handedness', e.target.value)}
                        >
                          <option value="">Valitse kätisyys</option>
                          <option value="Left">Vasen</option>
                          <option value="Right">Oikea</option>
                        </select>
                        <button type="button" onClick={() => savePlayer(player)} disabled={!socket}>
                          Tallenna
                        </button>
                        <button type="button" className="delete-button" onClick={() => deletePlayer(player.id, player.name)} disabled={!socket}>
                          Poista
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>Pelaajia ei löytynyt.</p>
      )}
    </div>
  );
}

export default Players;
